/**
 * Admin Console AuthContext — direct login (no OAuth redirect).
 * Authenticates via IC's /auth/team-login/ endpoint and keeps the login in
 * localStorage (utils/authStorage.js), so it survives reloads and new tabs.
 */
import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import axios from '../api/axios';
import {
  authGet,
  authSet,
  clearAuth,
  getAccessToken,
  getStoredUser,
} from '../utils/authStorage';

export const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authChecked, setAuthChecked] = useState(false);
  const [currentPath, setCurrentPath] = useState('/');

  const fetchUser = useCallback(async () => {
    if (!getAccessToken()) {
      setUser(null);
      setLoading(false);
      setAuthChecked(true);
      return;
    }

    try {
      const response = await axios.get('/auth/me/');
      const res = response?.data?.data;
      const userData = res?.user || response?.data;
      authSet('user_role', userData?.role);
      setUser(userData);
    } catch (err) {
      if (err.response?.status === 401) {
        clearAuth();
        setUser(null);
      }
      // Network trouble: keep the stored login rather than signing out.
    } finally {
      setLoading(false);
      setAuthChecked(true);
    }
  }, []);

  /** Called by the login page with the team-login response. */
  const signIn = useCallback((data) => {
    authSet('access_token', data.access_token);
    if (data.refresh_token) authSet('refresh_token', data.refresh_token);
    if (data.user) {
      authSet('platform_role', data.user.platform_role);
      authSet('admin_console_user', JSON.stringify(data.user));
      setUser(data.user);
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await axios.post('/auth/logout/', {}, { skipAuthRedirect: true });
    } catch {
      // Continue with frontend logout
    }
    clearAuth();
    try {
      localStorage.setItem('logout', Date.now().toString());
    } catch {
      /* storage unavailable */
    }
    setUser(null);
  }, []);

  // Restore the login on load (from this tab, another tab, or an older
  // sessionStorage login — authStorage moves that across).
  useEffect(() => {
    const token = getAccessToken();
    const storedUser = getStoredUser();
    if (token && storedUser) {
      setUser(storedUser);
    } else if (!token) {
      clearAuth();
    }
    setLoading(false);
    setAuthChecked(true);
  }, []);

  // Keep tabs in step: signing out in one tab signs out all of them,
  // and signing in on one tab signs in the others.
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'logout' && e.newValue) {
        clearAuth();
        setUser(null);
      }
      if (e.key === 'admin_console_user' && e.newValue && getAccessToken()) {
        const next = getStoredUser();
        if (next) setUser(next);
      }
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const platformRole = user?.platform_role || authGet('platform_role') || null;

  return (
    <AuthContext.Provider
      value={{
        user,
        setUser,
        signIn,
        logout,
        fetchUser,
        loading,
        authChecked,
        hasToken: !!getAccessToken(),
        currentPath,
        setCurrentPath,
        platformRole,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
